import { Box, Typography, Button, List, ListItem, ListItemText } from '@mui/material'
import { COLORS } from '../theme'
import {
  stepHeadingSx,
  stepDescriptionSx,
  primaryButtonSx,
  outlinedButtonSx,
} from '../styles'
import {
  CRAWL_FAILED_ERROR,
  CRAWL_NOT_STARTED_ERROR,
  type PageEntry,
} from '../hooks/useBatchScrape'
import { crawlFailureKind, type CrawlFailureKind } from '@/utils/websiteUploadErrors'

/** Pages that never made it in: failed, or crawled with nothing stored. */
export function failedPages(pages: PageEntry[]): PageEntry[] {
  return pages.filter((p) => p.status === 'error' || p.status === 'pending')
}

/**
 * A `pending` page here was crawled with nothing stored, or its job vanished.
 * Neither says the page is unusable, so both get the retry.
 */
export function canRetry(page: PageEntry): boolean {
  if (page.retried) return false
  return page.status === 'pending' || page.retryable === true
}

export type FailureKind =
  | 'unreachable'
  | 'unreadable'
  | 'tooLarge'
  | 'notFound'
  | 'unusable'
  | 'blocked'
  | 'notAllowed'
  | 'unknown'

// Causes the user can make sense of come first; the one we can't explain is last.
const KIND_ORDER: FailureKind[] = [
  'unreachable',
  'unreadable',
  'tooLarge',
  'notFound',
  'unusable',
  'blocked',
  'notAllowed',
  'unknown',
]

type KindCopy = {
  heading: [one: string, many: string]
  advice?: [one: string, many: string]
}

const PDF_ADVICE: [one: string, many: string] = [
  'Save it as a PDF and upload it as a document instead.',
  'Save them as PDFs and upload them as documents instead.',
]

const KIND_COPY: Record<FailureKind, KindCopy> = {
  unreachable: {
    heading: ['We couldn’t reach this page', 'We couldn’t reach these pages'],
    advice: [
      'Try again later, or save it as a PDF and upload it as a document.',
      'Try again later, or save them as PDFs and upload them as documents.',
    ],
  },
  unreadable: {
    heading: [
      'We couldn’t read any text on this page',
      'We couldn’t read any text on these pages',
    ],
    advice: [
      'It may be that this site uses protections that stop us reading it, or builds its content in a way we can’t read. ' +
        PDF_ADVICE[0],
      'It may be that these sites use protections that stop us reading them, or build their content in a way we can’t read. ' +
        PDF_ADVICE[1],
    ],
  },
  tooLarge: {
    heading: [
      'This page is too large for us to read',
      'These pages are too large for us to read',
    ],
    advice: PDF_ADVICE,
  },
  notFound: {
    heading: ['We couldn’t find this page', 'We couldn’t find these pages'],
    advice: ['Check the address is correct.', 'Check the addresses are correct.'],
  },
  unusable: {
    heading: [
      'We couldn’t use the content from this page',
      'We couldn’t use the content from these pages',
    ],
  },
  blocked: {
    heading: ['We can’t read pages from this site', 'We can’t read pages from these sites'],
    advice: PDF_ADVICE,
  },
  notAllowed: {
    heading: ['This address can’t be used', 'These addresses can’t be used'],
  },
  unknown: {
    heading: [
      'There was a problem trying to save this page',
      'There was a problem trying to save these pages',
    ],
  },
}

const CRAWL_FAILURE_GROUPS: Record<CrawlFailureKind, FailureKind> = {
  blocked: 'blocked',
  notFound: 'notFound',
  noText: 'unreadable',
  unreachable: 'unreachable',
  tooLarge: 'tooLarge',
  notUseful: 'unusable',
}

export function failureKind(page: PageEntry): FailureKind {
  if (page.status === 'pending') return 'unknown'
  const error = page.error ?? ''
  // Our own failures are retryable too, but the site was never at fault.
  if (error === CRAWL_NOT_STARTED_ERROR || error === CRAWL_FAILED_ERROR) return 'unknown'
  if (error.includes('Website URL not supported')) return 'blocked'
  if (error.includes('Website URL not allowed')) return 'notAllowed'
  const crawlKind = crawlFailureKind(error)
  if (crawlKind) return CRAWL_FAILURE_GROUPS[crawlKind]
  // A retryable failure the backend didn't name came from our side, not the site.
  if (page.retryable) return 'unknown'
  return 'unusable'
}

export function groupFailedPages(
  pages: PageEntry[],
): { kind: FailureKind; heading: string; advice?: string; pages: PageEntry[] }[] {
  return KIND_ORDER.map((kind) => {
    const inGroup = pages.filter((p) => failureKind(p) === kind)
    const { heading, advice } = KIND_COPY[kind]
    const index = inGroup.length === 1 ? 0 : 1
    return { kind, heading: heading[index], advice: advice?.[index], pages: inGroup }
  }).filter((group) => group.pages.length > 0)
}

interface FailedPagesNoticeProps {
  pages: PageEntry[]
  onRetry: () => void
  onContinue: () => void
  onBack: () => void
  /** Every page the user added failed, not just some. */
  allFailed: boolean
}

export default function FailedPagesNotice({
  pages,
  onRetry,
  onContinue,
  onBack,
  allFailed,
}: FailedPagesNoticeProps) {
  // With nothing from the website, fixing the URLs does more for the profile
  // than pressing on, so going back becomes the primary action.
  const backSx = allFailed ? primaryButtonSx : outlinedButtonSx
  const continueSx = allFailed ? outlinedButtonSx : primaryButtonSx
  const retryable = pages.some(canRetry)
  const fallback = allFailed
    ? 'we’ll build your profile from the other information you gave us, and ask you questions if we need more.'
    : 'your profile will be built from everything else you gave us.'
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        {allFailed
          ? 'None of your webpages made it in'
          : 'Some of your webpages didn’t make it in'}
      </Typography>
      <Typography variant="body1" sx={stepDescriptionSx}>
        {retryable ? (
          <>
            You can try again with the button below.
            <br />
            {`If you don’t want to try again, ${fallback}`}
          </>
        ) : (
          fallback.charAt(0).toUpperCase() + fallback.slice(1)
        )}
      </Typography>

      {groupFailedPages(pages).map((group) => (
        <Box key={group.kind} component="section" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            {group.heading}
          </Typography>
          {group.advice && (
            <Typography variant="body2" sx={{ color: COLORS.secondary }}>
              {group.advice}
            </Typography>
          )}
          <List disablePadding>
            {group.pages.map((page) => (
              <ListItem
                key={page.url}
                disableGutters
                sx={{ borderBottom: `1px solid ${COLORS.border}`, py: 1 }}
              >
                <ListItemText
                  primary={page.title || page.url}
                  secondary={page.title ? page.url : undefined}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { overflowWrap: 'anywhere' },
                  }}
                  secondaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}

      <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 3 }}>
        You can update your profile, files and websites any time after
        onboarding.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          disableRipple
          variant={allFailed ? 'contained' : 'outlined'}
          onClick={onBack}
          sx={backSx}
        >
          Go back to your webpages
        </Button>
        {retryable && (
          <Button
            disableRipple
            variant="outlined"
            onClick={onRetry}
            sx={outlinedButtonSx}
          >
            Try again
          </Button>
        )}
        <Button
          disableRipple
          variant={allFailed ? 'outlined' : 'contained'}
          onClick={onContinue}
          sx={continueSx}
        >
          Continue without them
        </Button>
      </Box>
    </Box>
  )
}
